'use client';

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import type { Product } from '@roomtreatment/av-catalog';
import {
  MAX_SUBS,
  ROLE_TYPES,
  SYSTEM_LAYOUTS,
  checkSystem,
  decodeSelection,
  encodeSelection,
  rolesForLayout,
  type SystemLayout,
  type SystemReport,
  type SystemRole,
  type SystemSelection,
} from '@roomtreatment/av-compat';
import Button, { buttonClass } from '@/components/ui/Button';
import Segmented from '@/components/room/chrome/Segmented';
import { optionChipClass } from '@/components/room/chrome/selection';
import { cachedCatalog, load, subscribe } from '@/components/room/gear/gearCatalogStore';
import { CONTENT, CONTENT_NAME, LINK, SECOND_LINE } from '@/components/seo/roles';
import { editorHref } from '@/components/seo/EditorCta';
import { EVENTS, track } from '@/lib/analytics';
import CheckerResults from './CheckerResults';
import ProductPicker from './ProductPicker';
import { LAYOUT_LABEL, ROLE_LABEL, gearHref, productLabel } from './checkerCopy';

// The product render is WebGL (three.js): client-only, loaded after the page,
// so it never weighs on first paint or blocks the result text.
const GearImageTile = dynamic(() => import('@/components/room/gear/GearImageTile'), {
  ssr: false,
  loading: () => <ThumbPlaceholder />,
});

function ThumbPlaceholder() {
  return <div className="aspect-square w-full rounded-lg bg-[var(--app-border)]" aria-hidden />;
}

interface Props {
  /** The example the server checked, so the page ships real results text. */
  initialSelection: SystemSelection;
  initialReport: SystemReport;
  /** The products `initialSelection` names, to label it before the catalogue loads. */
  initialProducts: readonly Product[];
}

type RunSource = 'picker' | 'link';

/** The hash is read once for the opening link; later writes are ours. */
const subscribeNever = () => () => undefined;

const SUB_COUNTS = Array.from({ length: MAX_SUBS }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));

export default function AvCompatibilityChecker({ initialSelection, initialReport, initialProducts }: Props) {
  const catalog = useSyncExternalStore(subscribe, cachedCatalog, () => null);
  const [selection, setSelection] = useState(initialSelection);
  const [openRole, setOpenRole] = useState<SystemRole | null>(null);
  const [copied, setCopied] = useState(false);
  const runSource = useRef<RunSource | null>(null);

  // The catalogue loads after hydration; a shared link waits for it so its ids
  // are checked against real product types before anything renders. The
  // server (and hydration) never sees a hash, so it renders the example.
  const [linkSettled, setLinkSettled] = useState(false);
  const hash = useSyncExternalStore(subscribeNever, () => window.location.hash, () => '');
  const loadingLink = !linkSettled && hash.length > 1;

  useEffect(() => {
    const linkHash = window.location.hash;
    let cancelled = false;
    load().then((loaded) => {
      if (cancelled) return;
      if (linkHash.length > 1) {
        runSource.current = 'link';
        setSelection(decodeSelection(linkHash, loaded));
      }
      setLinkSettled(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const report = useMemo(() => {
    if (selection === initialSelection) return initialReport;
    return catalog ? checkSystem(selection, catalog) : null;
  }, [selection, catalog, initialSelection, initialReport]);

  useEffect(() => {
    const source = runSource.current;
    if (!source || !report) return;
    runSource.current = null;
    track(EVENTS.compat_checker_run, {
      source,
      layout: selection.layout,
      verdict: report.verdict,
      issues: report.issues.length,
      unknown: report.unknown.length,
    });
  }, [report, selection.layout]);

  const byRole = useMemo(() => {
    if (!catalog) return null;
    const all = [...catalog.values()];
    const out = {} as Record<SystemRole, Product[]>;
    for (const role of Object.keys(ROLE_TYPES) as SystemRole[]) {
      out[role] = all.filter((p) => ROLE_TYPES[role].includes(p.type));
    }
    return out;
  }, [catalog]);

  const initialById = useMemo(() => new Map(initialProducts.map((p) => [p.id, p])), [initialProducts]);
  const product = (id: string | undefined) => (id ? (catalog?.get(id) ?? initialById.get(id)) : undefined);

  const update = (next: SystemSelection) => {
    runSource.current = 'picker';
    setSelection(next);
    setCopied(false);
    window.history.replaceState(null, '', `#${encodeSelection(next)}`);
  };

  const setLayout = (layout: SystemLayout) => {
    const kept = new Set(rolesForLayout(layout).map((r) => r.role));
    const next: SystemSelection = { layout };
    for (const { role } of rolesForLayout(selection.layout)) {
      if (!kept.has(role)) continue;
      if (role === 'amps') next.amps = selection.amps;
      else next[role] = selection[role];
    }
    if (next.sub && selection.subCount) next.subCount = selection.subCount;
    setOpenRole(null);
    update(next);
  };

  const pick = (role: SystemRole, id: string | undefined) => {
    const next: SystemSelection = { ...selection };
    if (role === 'amps') next.amps = id ? [id] : undefined;
    else next[role] = id;
    if (role === 'sub' && !id) next.subCount = undefined;
    setOpenRole(null);
    update(next);
  };

  const copyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}#${encodeSelection(selection)}`;
    track(EVENTS.compat_checker_shared, { layout: selection.layout });
    void navigator.clipboard?.writeText(url).then(() => setCopied(true), () => undefined);
  };

  // The system travels pre-encoded (encodeSelection), the same string the
  // "Copy link" hash already carries — so opening the editor and sharing a
  // link seed the same picked system, and this component never needs the
  // build engine itself: EditorCta.tsx's editorHref never pulls it in.
  const actions = (
    <>
      <Link
        href={editorHref(undefined, encodeSelection(selection))}
        data-cta="primary"
        data-testid="checker-editor-cta"
        onClick={() => track(EVENTS.compat_checker_editor_opened, { layout: selection.layout, verdict: report?.verdict ?? null })}
        className={buttonClass({ variant: 'primary', size: 'md', className: 'group' })}
      >
        Check it in your room in the 3D editor
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
      </Link>
      <span data-seo="share">
        <Button variant="secondary" size="md" onClick={copyLink} data-testid="checker-copy-link">
          {copied ? 'Link copied' : 'Copy link'}
        </Button>
      </span>
    </>
  );

  return (
    <div className="guided-theme grid gap-8 md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]" data-testid="av-compatibility-checker">
      <div className="space-y-6">
        <div className="space-y-2">
          <h3 className={CONTENT_NAME}>Speaker layout</h3>
          <div role="radiogroup" aria-label="Speaker layout" className="flex flex-wrap gap-2">
            {SYSTEM_LAYOUTS.map((layout) => (
              <button
                key={layout}
                type="button"
                role="radio"
                aria-checked={selection.layout === layout}
                data-testid={`checker-layout-${layout}`}
                onClick={() => selection.layout !== layout && setLayout(layout)}
                className={optionChipClass(selection.layout === layout)}
              >
                {LAYOUT_LABEL[layout]}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <h3 className={CONTENT_NAME}>Your components</h3>
          <ul className="space-y-4">
            {rolesForLayout(selection.layout).map(({ role, required }) => {
              const ids = role === 'amps' ? (selection.amps ?? []) : selection[role] ? [selection[role]] : [];
              const chosen = ids.map(product).filter((p): p is Product => !!p);
              const open = openRole === role;
              return (
                <li key={role} className="space-y-3" data-testid={`checker-role-${role}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 shrink-0">
                      {chosen[0] ? <GearImageTile product={chosen[0]} /> : <ThumbPlaceholder />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={SECOND_LINE}>{ROLE_LABEL[role]}</p>
                      <p className={`truncate ${CONTENT}`}>
                        {chosen.length === 0
                          ? 'Not chosen'
                          : chosen.map((p, i) => (
                            <span key={p.id}>
                              {i > 0 && ', '}
                              <Link href={gearHref(p.id)} className={LINK}>{productLabel(p)}</Link>
                            </span>
                          ))}
                      </p>
                    </div>
                    <Button
                      variant="secondary"
                      aria-expanded={open}
                      data-testid={`checker-choose-${role}`}
                      onClick={() => setOpenRole(open ? null : role)}
                    >
                      {open ? 'Close' : chosen.length > 0 ? 'Change' : 'Choose'}
                    </Button>
                  </div>
                  {role === 'sub' && selection.sub && (
                    <div className="flex items-center gap-3 pl-15">
                      <span className={SECOND_LINE}>How many</span>
                      <Segmented
                        items={SUB_COUNTS}
                        value={String(selection.subCount ?? 1)}
                        onChange={(v) => update({ ...selection, subCount: Number(v) > 1 ? Number(v) : undefined })}
                        testId="checker-sub-count"
                      />
                    </div>
                  )}
                  {open && (
                    <ProductPicker
                      label={ROLE_LABEL[role]}
                      products={byRole?.[role] ?? null}
                      selectedId={ids[0]}
                      optional={!required}
                      onPick={(id) => pick(role, id)}
                      testId={`checker-picker-${role}`}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div>
        {report && !loadingLink ? (
          <CheckerResults report={report} actions={actions} />
        ) : (
          <p className={SECOND_LINE}>Checking your system…</p>
        )}
      </div>
    </div>
  );
}
